import React, { useEffect, useMemo, useRef, useState } from "react";
import { ICONS } from "../../constants";
import { getStableAnimalEmoji } from "../../utils/utils";
import { getBuildingType } from "./islandMapping";
import { getImageRecord, requestImage } from "./spriteLoader";

const BUILDING_SCALE = 1.3;
const ROAD_W = 30; // keep in sync with the road stroke width below
const MAX_WORLD_SCALE = 1.18; // keep in sync with the render() clamp below

function hashStringToUint32(str) {
  // FNV-1a 32-bit
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function makeRng(seedStr) {
  let x = hashStringToUint32(String(seedStr || "0")) || 1;
  return () => {
    // xorshift32
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    // 0..1
    return (x >>> 0) / 4294967296;
  };
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function wobbleStrokeRoundedRect(ctx, rect, r, seed, amp = 5, step = 22) {
  if (!ctx || !rect) return;
  const { x, y, w, h } = rect;
  const rr = Math.max(0, Math.min(r || 0, w / 2, h / 2));
  const rng = makeRng(seed);

  const pts = [];
  const addEdge = (x1, y1, x2, y2, nx, ny) => {
    const len = Math.max(1e-6, Math.hypot(x2 - x1, y2 - y1));
    const n = Math.max(2, Math.ceil(len / Math.max(8, step)));
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const px = x1 + (x2 - x1) * t;
      const py = y1 + (y2 - y1) * t;
      const wob = (rng() - 0.5) * 2 * amp;
      pts.push({ x: px + nx * wob, y: py + ny * wob });
    }
  };

  const addArc = (cx, cy, a0, a1) => {
    const arcLen = Math.abs(a1 - a0) * rr;
    const n = Math.max(4, Math.ceil(arcLen / Math.max(8, step)));
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const a = a0 + (a1 - a0) * t;
      const bx = cx + Math.cos(a) * rr;
      const by = cy + Math.sin(a) * rr;
      const nx = Math.cos(a);
      const ny = Math.sin(a);
      const wob = (rng() - 0.5) * 2 * amp;
      pts.push({ x: bx + nx * wob, y: by + ny * wob });
    }
  };

  // Top edge + top-right corner
  addEdge(x + rr, y, x + w - rr, y, 0, -1);
  addArc(x + w - rr, y + rr, -Math.PI / 2, 0);
  // Right edge + bottom-right corner
  addEdge(x + w, y + rr, x + w, y + h - rr, 1, 0);
  addArc(x + w - rr, y + h - rr, 0, Math.PI / 2);
  // Bottom edge + bottom-left corner
  addEdge(x + w - rr, y + h, x + rr, y + h, 0, 1);
  addArc(x + rr, y + h - rr, Math.PI / 2, Math.PI);
  // Left edge + top-left corner
  addEdge(x, y + h - rr, x, y + rr, -1, 0);
  addArc(x + rr, y + rr, Math.PI, (3 * Math.PI) / 2);

  if (pts.length < 3) return;

  // Smooth closed curve via quadratic midpoints
  ctx.beginPath();
  const p0 = pts[0];
  ctx.moveTo(p0.x, p0.y);
  for (let i = 1; i <= pts.length; i += 1) {
    const prev = pts[i - 1];
    const curr = pts[i % pts.length];
    const mx = (prev.x + curr.x) / 2;
    const my = (prev.y + curr.y) / 2;
    ctx.quadraticCurveTo(prev.x, prev.y, mx, my);
  }
  ctx.closePath();
  ctx.stroke();
}

function addNotchedRoundRectPath(ctx, x, y, w, h, r, notch) {
  // Adds two subpaths: the outer rounded rect + an optional circular notch "hole".
  // Use ctx.fill("evenodd") or ctx.clip("evenodd") so the notch becomes a cutout.
  roundRect(ctx, x, y, w, h, r);
  if (notch && isFinite(notch.cx) && isFinite(notch.cy) && isFinite(notch.r) && notch.r > 0) {
    ctx.moveTo(notch.cx + notch.r, notch.cy);
    ctx.arc(notch.cx, notch.cy, notch.r, 0, Math.PI * 2);
  }
}

function formatMoney(amountCents) {
  const n = typeof amountCents === "string" ? Number(amountCents) : amountCents;
  if (!isFinite(n)) return "";
  return `$${(n / 100).toFixed(2)}`;
}

function scaledPattern(ctx, img, targetSize) {
  if (!ctx || !img || !targetSize) return null;
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return null;

  const scale = targetSize / w;
  if (!isFinite(scale) || scale <= 0) return null;

  // Modern browsers: transform the pattern (fast, no extra canvas).
  const p = ctx.createPattern(img, "repeat");
  if (p && typeof p.setTransform === "function" && typeof DOMMatrix !== "undefined") {
    p.setTransform(new DOMMatrix().scale(scale));
    return p;
  }

  // Fallback: raster-scale into a temporary canvas, then pattern from that.
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));
  const tmp = document.createElement("canvas");
  tmp.width = outW;
  tmp.height = outH;
  const tctx = tmp.getContext("2d");
  if (!tctx) return p;
  tctx.drawImage(img, 0, 0, outW, outH);
  return ctx.createPattern(tmp, "repeat");
}

function cssVar(el, name, fallback) {
  try {
    const base = el || (typeof document !== "undefined" ? document.documentElement : null);
    if (!base || typeof getComputedStyle === "undefined") return fallback;
    const v = getComputedStyle(base).getPropertyValue(name);
    const out = String(v || "").trim();
    return out || fallback;
  } catch {
    return fallback;
  }
}

function getPlateTheme(el) {
  return {
    bg: cssVar(el, "--as-surface-92", "rgba(255, 255, 255, 0.92)"),
    border: cssVar(el, "--as-border-strong", "rgba(0, 0, 0, 0.12)"),
    text: cssVar(el, "--as-ink", "rgba(0, 0, 0, 0.82)"),
  };
}

function drawAnimalCrossingPlate(ctx, { x, y, w, h, text, textPadX = 12 }) {
  if (!ctx || !text) return;
  ctx.save();

  // Stronger shadow for "special" buildings
  ctx.shadowColor = "rgba(0,0,0,0.22)";
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 9;

  // Outer green trim (very distinct vs the default white pill)
  ctx.fillStyle = "rgba(108, 156, 86, 0.92)";
  ctx.strokeStyle = "rgba(42, 72, 34, 0.32)";
  ctx.lineWidth = 2.2;
  roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.stroke();

  // Inner cream panel
  const inset = 2.6;
  const ix = x + inset;
  const iy = y + inset;
  const iw = Math.max(1, w - inset * 2);
  const ih = Math.max(1, h - inset * 2);
  ctx.shadowColor = "transparent";
  const bg = ctx.createLinearGradient(ix, iy, ix, iy + ih);
  bg.addColorStop(0, "rgba(255, 250, 236, 0.98)");
  bg.addColorStop(1, "rgba(248, 241, 222, 0.96)");
  ctx.fillStyle = bg;
  ctx.strokeStyle = "rgba(86, 70, 44, 0.20)";
  ctx.lineWidth = 1;
  roundRect(ctx, ix, iy, iw, ih, 10);
  ctx.fill();
  ctx.stroke();

  // Subtle top highlight + bottom shade
  ctx.globalAlpha = 0.28;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.moveTo(ix + 10, iy + 5);
  ctx.lineTo(ix + iw - 10, iy + 5);
  ctx.stroke();
  ctx.globalAlpha = 0.16;
  ctx.strokeStyle = "rgba(0,0,0,0.6)";
  ctx.beginPath();
  ctx.moveTo(ix + 10, iy + ih - 5);
  ctx.lineTo(ix + iw - 10, iy + ih - 5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Little rivets on the green trim
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.beginPath();
  ctx.arc(x + 10, y + h / 2, 1.6, 0, Math.PI * 2);
  ctx.arc(x + w - 10, y + h / 2, 1.6, 0, Math.PI * 2);
  ctx.fill();

  // Text (slight shadow for readability)
  ctx.shadowColor = "rgba(0,0,0,0.18)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = "rgba(33, 28, 18, 0.90)";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.font = "900 11px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText(text, x + textPadX, y + h / 2 + 0.5);

  ctx.restore();
}

function drawSpecialBuildingShine(ctx, { x, y, w, h, ts, seed, layer = "under" }) {
  if (!ctx) return;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const base = hashStringToUint32(seed || "shine") / 4294967296;
  const t = (ts || 0) / 1000;
  const pulse = 0.6 + 0.4 * Math.sin(t * 2.2 + base * Math.PI * 2);

  if (layer === "under") {
    const r = Math.max(w, h) * (0.78 + 0.08 * Math.sin(t * 1.3 + base));

    // Stronger halo so it reads on bright backgrounds
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = 0.95;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, `rgba(255, 255, 220, ${0.22 * pulse})`);
    g.addColorStop(0.35, `rgba(205, 255, 205, ${0.18 * pulse})`);
    g.addColorStop(0.7, `rgba(180, 240, 255, ${0.10 * pulse})`);
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Sparkles around the sprite bounds
    const sparkleCount = 4;
    const spin = t * 0.45;
    for (let i = 0; i < sparkleCount; i += 1) {
      const tt = (base * 10 + i * 1.7 + spin) % 1;
      const ang = tt * Math.PI * 2;
      const sr = Math.max(w, h) * (0.34 + 0.10 * ((base * 7 + i) % 1));
      const sx = cx + Math.cos(ang) * sr;
      const sy = cy + Math.sin(ang) * sr * 0.75;
      const sPulse = 0.5 + 0.5 * Math.sin(t * 4.2 + i * 2 + base * 6);
      const sSize = 7 + 7 * sPulse;
      ctx.save();
      ctx.globalAlpha = 0.35 + 0.35 * sPulse;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(sx - sSize, sy);
      ctx.lineTo(sx + sSize, sy);
      ctx.moveTo(sx, sy - sSize);
      ctx.lineTo(sx, sy + sSize);
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
    return;
  }

  // layer === "over": moving sheen on top of the sprite
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.globalAlpha = 0.55 * pulse;

  // Clip to sprite rect so sheen stays on the building art
  roundRect(ctx, x, y, w, h, 14);
  ctx.clip();

  const sweep = ((t * 0.55 + base) % 1) * (w + h) - h;
  const grad = ctx.createLinearGradient(x + sweep, y, x + sweep + h, y + h);
  grad.addColorStop(0, "rgba(255,255,255,0)");
  grad.addColorStop(0.45, "rgba(255,255,255,0.0)");
  grad.addColorStop(0.55, "rgba(255,255,255,0.85)");
  grad.addColorStop(0.65, "rgba(255,255,255,0.0)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(x - h, y, w + h * 2, h);

  ctx.restore();
}

function makeNodeKey(x, y) {
  // Keep stable keys for float coordinates
  return `${x.toFixed(2)},${y.toFixed(2)}`;
}

function buildRoadGraphFromSegments(segments) {
  const nodes = [];
  const nodeIndexByKey = new Map();
  const adj = [];

  const addNode = (x, y) => {
    const key = makeNodeKey(x, y);
    const existing = nodeIndexByKey.get(key);
    if (existing !== undefined) return existing;
    const idx = nodes.length;
    nodeIndexByKey.set(key, idx);
    nodes.push({ x, y });
    adj.push([]);
    return idx;
  };

  const addUndirectedEdge = (aIdx, bIdx) => {
    if (aIdx === bIdx) return;
    const ax = nodes[aIdx].x;
    const ay = nodes[aIdx].y;
    const bx = nodes[bIdx].x;
    const by = nodes[bIdx].y;
    const d = Math.max(1e-6, Math.hypot(bx - ax, by - ay));
    adj[aIdx].push({ to: bIdx, d });
    adj[bIdx].push({ to: aIdx, d });
  };

  for (let i = 0; i < segments.length; i += 1) {
    const s = segments[i];
    const a = addNode(s.x1, s.y1);
    const b = addNode(s.x2, s.y2);
    addUndirectedEdge(a, b);
  }

  return { nodes, adj };
}

function pickNextNeighbor({ adj, atIdx, prevIdx, seed }) {
  const neighbors = adj[atIdx] || [];
  if (neighbors.length === 0) return null;
  const filtered =
    prevIdx !== null && prevIdx !== undefined
      ? neighbors.filter((n) => n.to !== prevIdx)
      : neighbors;
  const list = filtered.length > 0 ? filtered : neighbors;
  const rng = makeRng(seed);
  const pick = Math.floor(rng() * list.length);
  return list[pick]?.to ?? null;
}

export default function IslandCanvas(props) {
  const {
    contributors,
    transactions,
    sceneVariant,
    isSettled = false,
    onSelectTransaction,
    onSelectContributor,
    onCreateTransaction,
    onInviteContributor,
    onOpenVisitorCenter,
    onOpenGameCenter,
  } = props;

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);
  const animalsRef = useRef([]);
  const transformRef = useRef({ scale: 1, ox: 0, oy: 0, worldW: 1, worldH: 1 });
  const hoverTxnIdRef = useRef(null);

  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) => {
        const cr = entries?.[0]?.contentRect;
        if (!cr) return;
        setSize({ w: Math.round(cr.width), h: Math.round(cr.height) });
      })
      : null;

    if (ro) ro.observe(el);

    const onResize = () => {
      const rect = el.getBoundingClientRect();
      setSize({ w: Math.round(rect.width), h: Math.round(rect.height) });
    };

    window.addEventListener("resize", onResize);
    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect?.();
    };
  }, []);

  const layout = useMemo(() => {
    const txs = Array.isArray(transactions) ? transactions : [];
    const n = txs.length;

    // Fixed 3-column "town" grid:
    // - Always 3 columns (better readability and bigger buildings on small screens)
    // - Add rows as needed; we fill from bottom-up so extra rows effectively appear at the top.
    // - Second row from bottom, right tile is reserved for Construction
    const cols = 3;
    // Special buildings occupy cells (Construction + Visitor Center + Game Center).
    const tilesNeeded = n + 3; // transactions + 3 action buildings
    const rows = Math.max(3, Math.ceil(Math.max(1, tilesNeeded) / cols));

    const cellW = 180;
    const cellH = Math.round(150 * BUILDING_SCALE);
    const pad = 44;
    const buildingW = Math.round(118 * BUILDING_SCALE);
    // Taller hitbox so the bottom-left amount plaque can sit below the building sprite with spacing
    const buildingH = Math.round(120 * BUILDING_SCALE);

    const worldW = pad * 2 + cols * cellW;
    const worldH = pad * 2 + rows * cellH;

    const reservedRow = Math.max(0, rows - 2); // second row from bottom
    const reservedCol = cols - 1; // right-most
    const visitorRow = 0;
    const visitorCol = cols - 1; // top-right
    const gameCenterRow = rows - 1;
    const gameCenterCol = 0; // bottom-left

    const sorted = [...txs].sort((a, b) => String(a?.id || "").localeCompare(String(b?.id || "")));

    // Build list of available tiles, skipping reserved ones.
    const reserved = new Set([
      `${reservedRow},${reservedCol}`,
      `${visitorRow},${visitorCol}`,
      `${gameCenterRow},${gameCenterCol}`,
    ]);
    const availableTiles = [];
    for (let r = rows - 1; r >= 0; r -= 1) {
      for (let c = 0; c < cols; c += 1) {
        const key = `${r},${c}`;
        if (reserved.has(key)) continue;
        availableTiles.push({ r, c });
      }
    }

    // Nudge buildings downward so the top name plate doesn't collide with the top road edge.
    const buildingYOffset = Math.round(12 * BUILDING_SCALE);
    const buildings = [];

    // Place action buildings first (fixed tiles)
    const tileToXY = (r, c) => {
      const cx = pad + c * cellW + cellW / 2;
      const cy = pad + r * cellH + cellH / 2;
      return { cx, cy };
    };

    {
      const { cx, cy } = tileToXY(reservedRow, reservedCol);
      buildings.push({
        kind: "action",
        action: "create_transaction",
        tile: { r: reservedRow, c: reservedCol },
        x: cx - buildingW / 2,
        y: cy - buildingH / 2 + buildingYOffset,
        w: buildingW,
        h: buildingH,
      });
    }

    // Visitor Center: pinned to top-right tile
    {
      const { cx, cy } = tileToXY(visitorRow, visitorCol);
      buildings.push({
        kind: "action",
        action: "visitor_center",
        tile: { r: visitorRow, c: visitorCol },
        x: cx - buildingW / 2,
        y: cy - buildingH / 2 + buildingYOffset,
        w: buildingW,
        h: buildingH,
      });
    }

    // Game Center: pinned to bottom-left tile
    {
      const { cx, cy } = tileToXY(gameCenterRow, gameCenterCol);
      buildings.push({
        kind: "action",
        action: "game_center",
        tile: { r: gameCenterRow, c: gameCenterCol },
        x: cx - buildingW / 2,
        y: cy - buildingH / 2 + buildingYOffset,
        w: buildingW,
        h: buildingH,
      });
    }

    // Place transaction buildings into remaining tiles deterministically.
    for (let i = 0; i < sorted.length; i += 1) {
      const tx = sorted[i];
      const t = availableTiles[i];
      if (!t) break;
      const { cx, cy } = tileToXY(t.r, t.c);
      buildings.push({
        kind: "transaction",
        transaction: tx,
        tile: { r: t.r, c: t.c },
        x: cx - buildingW / 2,
        y: cy - buildingH / 2 + buildingYOffset,
        w: buildingW,
        h: buildingH,
      });
    }

    return { worldW, worldH, buildings, rows, cols, cellW, cellH, pad };
  }, [transactions]);

  const beach = useMemo(() => {
    const worldW = layout.worldW;
    const worldH = layout.worldH;

    // Inset constants (world-space). Keep these in sync with draw order below.
    // Wider beach: increase the sand band thickness (grassInset - beachInset).
    const waterInset = 10;
    const beachInset = 22;
    const grassInset = 52;

    const outer = {
      x: beachInset,
      y: beachInset,
      w: worldW - beachInset * 2,
      h: worldH - beachInset * 2,
      r: 24,
    };

    const inner = {
      x: grassInset,
      y: grassInset,
      w: worldW - grassInset * 2,
      h: worldH - grassInset * 2,
      r: 20,
    };

    // Deterministic "sand speckles" so the beach has texture but doesn't shimmer.
    const rng = makeRng(`beach:${worldW}x${worldH}`);
    const speckles = [];
    const count = Math.max(120, Math.min(260, Math.floor((worldW * worldH) / 26000)));
    for (let i = 0; i < count; i += 1) {
      const x = outer.x + rng() * outer.w;
      const y = outer.y + rng() * outer.h;

      // Skip points that fall in the grass interior (leave a little buffer).
      const buffer = 6;
      if (
        x >= inner.x + buffer &&
        x <= inner.x + inner.w - buffer &&
        y >= inner.y + buffer &&
        y <= inner.y + inner.h - buffer
      ) {
        continue;
      }

      speckles.push({
        x,
        y,
        r: 0.8 + rng() * 1.6,
        a: 0.10 + rng() * 0.18,
      });
    }

    return {
      waterInset,
      beachInset,
      grassInset,
      outer,
      inner,
      speckles,
      // No harbor-specific bay cutout. Keep API stable for drawing calls.
      notchOuter: null,
      notchInner: null,
    };
  }, [layout.worldW, layout.worldH]);

  const harborBuilding = useMemo(() => {
    // Position Harbor on the beach (outside the town grid), near the bottom-right shoreline.
    // Keep the same hitbox sizing as grid buildings so interactions stay consistent.
    const buildingW = Math.round(118 * BUILDING_SCALE);
    const buildingH = Math.round(120 * BUILDING_SCALE);
    // Push it all the way into the island corner so it won't overlap the bottom-right grid cell.
    // (It's ok if it sits partially over water/sand — it’s meant to feel “on the shore”.)
    const margin = 6;
    const x = layout.worldW - margin - buildingW + Math.round(45 * BUILDING_SCALE);
    const y = layout.worldH - margin - buildingH + Math.round(20 * BUILDING_SCALE);
    return {
      kind: "action",
      action: "invite_contributor",
      tile: null,
      x,
      y,
      w: buildingW,
      h: buildingH,
    };
  }, [layout.worldW, layout.worldH]);

  const buildingsToDraw = useMemo(() => {
    return [...(layout.buildings || []), harborBuilding].filter(Boolean);
  }, [layout.buildings, harborBuilding]);

  const roadNetwork = useMemo(() => {
    const buildings = (layout.buildings || []).filter(
      (b) => (b?.kind === "transaction" && b?.transaction) || (b?.kind === "action" && b?.action)
    );

    // Roads live on cell edges (grid lines).
    // IMPORTANT: We intentionally avoid drawing roads on the *outer perimeter* of the grid,
    // because the grid can extend into the beach band (sand). Roads should stay on the grass interior.
    const segKeys = new Set();

    const { rows, cols } = layout;
    for (let i = 0; i < buildings.length; i += 1) {
      const b = buildings[i];
      const r = b?.tile?.r;
      const c = b?.tile?.c;
      if (!isFinite(r) || !isFinite(c)) continue;
      if (r < 0 || r >= rows || c < 0 || c >= cols) continue;

      // Add only *internal* edges (between cells), not the grid perimeter.
      // left edge exists only if there's a cell to the left
      if (c > 0) segKeys.add(`V:${r}:${c}`);
      // right edge exists only if there's a cell to the right
      if (c < cols - 1) segKeys.add(`V:${r}:${c + 1}`);
      // top edge exists only if there's a cell above
      if (r > 0) segKeys.add(`H:${r}:${c}`);
      // bottom edge exists only if there's a cell below
      if (r < rows - 1) segKeys.add(`H:${r + 1}:${c}`);
    }

    const segments = [];
    const { cellW, cellH, pad } = layout;

    segKeys.forEach((key) => {
      const [kind, a, b] = String(key).split(":");
      const i1 = Number(a);
      const i2 = Number(b);
      if (!isFinite(i1) || !isFinite(i2)) return;

      if (kind === "V") {
        // V:row:boundaryCol
        const r = i1;
        const k = i2;
        if (r < 0 || r >= rows) return;
        if (k < 0 || k > cols) return;
        const x = pad + k * cellW;
        const y1 = pad + r * cellH;
        const y2 = y1 + cellH;
        segments.push({ x1: x, y1, x2: x, y2 });
      } else if (kind === "H") {
        // H:boundaryRow:col
        const k = i1;
        const c = i2;
        if (c < 0 || c >= cols) return;
        if (k < 0 || k > rows) return;
        const y = pad + k * cellH;
        const x1 = pad + c * cellW;
        const x2 = x1 + cellW;
        segments.push({ x1, y1: y, x2, y2: y });
      }
    });

    const graph = buildRoadGraphFromSegments(segments);
    return { segments, graph };
  }, [layout]);

  const wrapHeightPx = useMemo(() => {
    // Size the canvas wrapper so the ocean background doesn't dominate when the island is small.
    // We match the height to the island's aspect ratio at the scale we'd use if width is limiting.
    const margin = 18; // keep consistent with render() transform margin
    const cw = size.w || 0;
    if (!cw || !layout?.worldW || !layout?.worldH) return 560;

    const scaleByWidth = clamp((cw - margin * 2) / layout.worldW, 0.25, MAX_WORLD_SCALE);
    const desired = layout.worldH * scaleByWidth + margin * 2;

    const vhCap =
      typeof window !== "undefined" && window?.innerHeight
        ? window.innerHeight * 0.78
        : 720;

    return Math.round(clamp(desired, 360, Math.min(900, vhCap)));
  }, [size.w, layout.worldW, layout.worldH]);

  useEffect(() => {
    const list = Array.isArray(contributors) ? contributors : [];
    const worldW = layout.worldW;
    const worldH = layout.worldH;
    const next = list.map((c) => {
      const rng = makeRng(`contrib:${c?.id || c?.name || ""}`);
      const speed = 18 + rng() * 22;

      // If we have a road graph, spawn on a road node and walk edge-to-edge.
      const hasRoads = (roadNetwork?.graph?.nodes?.length || 0) > 1;
      if (hasRoads) {
        const nodes = roadNetwork.graph.nodes;
        const idx = hashStringToUint32(`walk:${c?.id || c?.name || ""}`) % nodes.length;
        const start = nodes[idx];
        const nextIdx = pickNextNeighbor({
          adj: roadNetwork.graph.adj,
          atIdx: idx,
          prevIdx: null,
          seed: `turn:${c?.id || c?.name || ""}:0`,
        });

        return {
          id: c?.id,
          contributor: c,
          x: start.x,
          y: start.y,
          speed,
          road: {
            enabled: true,
            atIdx: idx,
            nextIdx,
            prevIdx: null,
            turns: 0,
          },
        };
      }

      // Fallback: free roam
      const x = 30 + rng() * Math.max(1, worldW - 60);
      const y = 30 + rng() * Math.max(1, worldH - 60);
      const ang = rng() * Math.PI * 2;
      return {
        id: c?.id,
        contributor: c,
        x,
        y,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        speed,
        road: { enabled: false },
      };
    });
    animalsRef.current = next;
  }, [contributors, layout.worldW, layout.worldH, roadNetwork]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!size.w || !size.h) return;
    canvas.width = Math.max(1, Math.floor(size.w * window.devicePixelRatio));
    canvas.height = Math.max(1, Math.floor(size.h * window.devicePixelRatio));
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
  }, [size]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const render = (ts) => {
      rafRef.current = window.requestAnimationFrame(render);

      const dpr = window.devicePixelRatio || 1;
      const cw = canvas.width / dpr;
      const ch = canvas.height / dpr;
      if (!cw || !ch) return;

      const worldW = layout.worldW;
      const worldH = layout.worldH;

      const margin = 18;
      const scale = clamp(
        Math.min((cw - margin * 2) / worldW, (ch - margin * 2) / worldH),
        0.25,
        MAX_WORLD_SCALE
      );
      const ox = (cw - worldW * scale) / 2;
      const oy = (ch - worldH * scale) / 2;
      transformRef.current = { scale, ox, oy, worldW, worldH };

      const lastTs = lastTsRef.current || ts;
      const dt = clamp((ts - lastTs) / 1000, 0, 0.05);
      lastTsRef.current = ts;

      // Update animals
      const centerX = worldW / 2;
      const centerY = worldH / 2;
      const animals = animalsRef.current;
      for (let i = 0; i < animals.length; i += 1) {
        const a = animals[i];

        // Road-walking mode: move between graph nodes
        if (a?.road?.enabled && roadNetwork?.graph?.nodes?.length > 1) {
          const nodes = roadNetwork.graph.nodes;
          const adj = roadNetwork.graph.adj;
          const atIdx = a.road.atIdx;
          const nextIdx = a.road.nextIdx;

          if (nextIdx === null || nextIdx === undefined) {
            const newNext = pickNextNeighbor({
              adj,
              atIdx,
              prevIdx: a.road.prevIdx,
              seed: `turn:${a?.id || i}:${a.road.turns}`,
            });
            a.road.nextIdx = newNext;
            continue;
          }

          const tx = nodes[nextIdx].x;
          const ty = nodes[nextIdx].y;
          const dx = tx - a.x;
          const dy = ty - a.y;
          const dist = Math.max(1e-6, Math.hypot(dx, dy));
          const step = (a.speed || 28) * dt;

          if (step >= dist) {
            a.x = tx;
            a.y = ty;
            a.road.prevIdx = atIdx;
            a.road.atIdx = nextIdx;
            a.road.turns = (a.road.turns || 0) + 1;
            a.road.nextIdx = pickNextNeighbor({
              adj,
              atIdx: nextIdx,
              prevIdx: atIdx,
              seed: `turn:${a?.id || i}:${a.road.turns}`,
            });
          } else {
            a.x += (dx / dist) * step;
            a.y += (dy / dist) * step;
          }

          continue;
        }

        const ax = (centerX - a.x) * 0.015;
        const ay = (centerY - a.y) * 0.015;
        a.vx += ax * dt;
        a.vy += ay * dt;
        const v = Math.hypot(a.vx, a.vy);
        const maxV = 48;
        if (v > maxV) {
          a.vx = (a.vx / v) * maxV;
          a.vy = (a.vy / v) * maxV;
        }
        a.x += a.vx * dt;
        a.y += a.vy * dt;

        const r = 14;
        if (a.x < r) {
          a.x = r;
          a.vx *= -1;
        }
        if (a.x > worldW - r) {
          a.x = worldW - r;
          a.vx *= -1;
        }
        if (a.y < r) {
          a.y = r;
          a.vy *= -1;
        }
        if (a.y > worldH - r) {
          a.y = worldH - r;
          a.vy *= -1;
        }
      }

      // Clear
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, ch);

      // Background
      const oceanTileUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/ocean_tile.png`;
      requestImage(oceanTileUrl);
      const oceanTile = getImageRecord(oceanTileUrl);
      if (oceanTile.status === "loaded" && oceanTile.img) {
        const p = scaledPattern(ctx, oceanTile.img, 96); // screen-space tile size (px)
        if (p) {
          ctx.fillStyle = p;
          ctx.fillRect(0, 0, cw, ch);
        }
      } else {
        const bg = ctx.createLinearGradient(0, 0, 0, ch);
        bg.addColorStop(0, "rgba(35, 171, 225, 0.55)");
        bg.addColorStop(0.55, "rgba(39, 152, 220, 0.48)");
        bg.addColorStop(1, "rgba(18, 88, 170, 0.42)");
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, cw, ch);

        // Subtle wave bands (screen-space so they don't jitter with scaling)
        ctx.save();
        ctx.globalAlpha = 0.12;
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.lineWidth = 1;
        const waveOffset = (ts / 900) % 120;
        for (let y = -40; y < ch + 60; y += 34) {
          ctx.beginPath();
          const baseY = y + waveOffset;
          for (let x = -20; x <= cw + 20; x += 40) {
            const amp = 6;
            const y1 = baseY + Math.sin((x + ts / 40) / 48) * amp;
            if (x === -20) ctx.moveTo(x, y1);
            else ctx.quadraticCurveTo(x - 20, y1, x, y1);
          }
          ctx.stroke();
        }
        ctx.restore();
      }

      // World transform
      ctx.translate(ox, oy);
      ctx.scale(scale, scale);

      // Draw near-shore water around the island
      ctx.save();
      const nearWaterGrad = ctx.createLinearGradient(0, 0, 0, worldH);
      nearWaterGrad.addColorStop(0, "rgba(86, 210, 232, 0.55)");
      nearWaterGrad.addColorStop(1, "rgba(30, 152, 220, 0.40)");
      ctx.fillStyle = nearWaterGrad;
      roundRect(
        ctx,
        beach.waterInset,
        beach.waterInset,
        worldW - beach.waterInset * 2,
        worldH - beach.waterInset * 2,
        28
      );
      ctx.fill();
      ctx.restore();

      // Optional shoreline foam overlay (Animal Crossing feel)
      const foamTileUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/shore_foam_tile.png`;
      requestImage(foamTileUrl);
      const foamTile = getImageRecord(foamTileUrl);
      if (foamTile.status === "loaded" && foamTile.img) {
        ctx.save();
        // Clip to ring: inside water rect, outside beach rect
        ctx.beginPath();
        roundRect(
          ctx,
          beach.waterInset,
          beach.waterInset,
          worldW - beach.waterInset * 2,
          worldH - beach.waterInset * 2,
          28
        );
        addNotchedRoundRectPath(
          ctx,
          beach.outer.x,
          beach.outer.y,
          beach.outer.w,
          beach.outer.h,
          beach.outer.r,
          beach.notchOuter
        );
        ctx.clip("evenodd");

        const p = scaledPattern(ctx, foamTile.img, 120); // world-space-ish size
        if (p) {
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = p;
          ctx.fillRect(0, 0, worldW, worldH);
        }
        ctx.restore();
      }

      // Beach ring
      ctx.save();
      const sandTileUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/sand_tile.png`;
      requestImage(sandTileUrl);
      const sandTile = getImageRecord(sandTileUrl);
      if (sandTile.status === "loaded" && sandTile.img) {
        const p = scaledPattern(ctx, sandTile.img, 86); // world-space tile size
        if (p) ctx.fillStyle = p;
      } else {
        const sandGrad = ctx.createLinearGradient(0, 0, 0, worldH);
        sandGrad.addColorStop(0, "rgba(255, 232, 175, 0.62)");
        sandGrad.addColorStop(1, "rgba(244, 214, 150, 0.52)");
        ctx.fillStyle = sandGrad;
      }
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.outer.x,
        beach.outer.y,
        beach.outer.w,
        beach.outer.h,
        beach.outer.r,
        beach.notchOuter
      );
      ctx.fill("evenodd");

      // Wobbly coastline highlight (deterministic) for a more organic shore.
      ctx.save();
      // Clip to sand so we don't accidentally draw in the harbor "bay" cutout.
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.outer.x,
        beach.outer.y,
        beach.outer.w,
        beach.outer.h,
        beach.outer.r,
        beach.notchOuter
      );
      ctx.clip("evenodd");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = 0.38;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
      ctx.lineWidth = 7;
      wobbleStrokeRoundedRect(
        ctx,
        beach.outer,
        beach.outer.r,
        `shore:outer:${worldW}x${worldH}`,
        6,
        26
      );
      ctx.restore();

      // Sand speckles (clipped to beach area, excluding grass)
      ctx.save();
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.outer.x,
        beach.outer.y,
        beach.outer.w,
        beach.outer.h,
        beach.outer.r,
        beach.notchOuter
      );
      ctx.clip("evenodd");
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      for (let i = 0; i < beach.speckles.length; i += 1) {
        const p = beach.speckles[i];
        // Skip anything that lands in the grass interior
        if (
          p.x >= beach.inner.x &&
          p.x <= beach.inner.x + beach.inner.w &&
          p.y >= beach.inner.y &&
          p.y <= beach.inner.y + beach.inner.h
        ) {
          continue;
        }
        ctx.globalAlpha = p.a;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      ctx.restore();

      // Grass (island interior)
      ctx.save();
      const grassTileUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/grass_tile.png`;
      requestImage(grassTileUrl);
      const grassTile = getImageRecord(grassTileUrl);
      if (grassTile.status === "loaded" && grassTile.img) {
        const p = scaledPattern(ctx, grassTile.img, 78); // world-space tile size
        if (p) ctx.fillStyle = p;
      } else {
        ctx.fillStyle = "rgba(146, 203, 76, 0.22)";
      }
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.inner.x,
        beach.inner.y,
        beach.inner.w,
        beach.inner.h,
        22,
        beach.notchInner
      );
      ctx.fill("evenodd");

      // Slightly wobbly grass edge to break the perfect ring.
      ctx.save();
      // Clip to grass so the stroke doesn't draw across the harbor bay cutout.
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.inner.x,
        beach.inner.y,
        beach.inner.w,
        beach.inner.h,
        22,
        beach.notchInner
      );
      ctx.clip("evenodd");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = 0.18;
      ctx.strokeStyle = "rgba(160, 130, 88, 0.95)"; // sandy edge shadow
      ctx.lineWidth = 6;
      wobbleStrokeRoundedRect(
        ctx,
        beach.inner,
        22,
        `shore:inner:${worldW}x${worldH}`,
        4,
        28
      );
      ctx.restore();

      ctx.restore();

      // Stone paths connecting buildings (drawn on grass only)
      ctx.save();
      // Clip to grass interior
      ctx.beginPath();
      addNotchedRoundRectPath(
        ctx,
        beach.inner.x,
        beach.inner.y,
        beach.inner.w,
        beach.inner.h,
        22,
        beach.notchInner
      );
      ctx.clip("evenodd");

      const stoneTileUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/stone_path_tile.png`;
      requestImage(stoneTileUrl);
      const stoneTile = getImageRecord(stoneTileUrl);
      const stonePattern =
        stoneTile.status === "loaded" && stoneTile.img
          ? scaledPattern(ctx, stoneTile.img, 70)
          : null;

      const segments = roadNetwork?.segments || [];
      if (segments.length > 0) {
        // Soft shadow under the path for depth
        ctx.save();
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "rgba(0, 0, 0, 0.12)";
        ctx.lineWidth = 40; // wider roads
        for (let i = 0; i < segments.length; i += 1) {
          const s = segments[i];
          ctx.beginPath();
          ctx.moveTo(s.x1, s.y1);
          ctx.lineTo(s.x2, s.y2);
          ctx.stroke();
        }
        ctx.restore();

        // Actual path stroke (pattern if available)
        ctx.save();
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const roadW = ROAD_W; // wider roads

        // IMPORTANT: When stroking with a semi-transparent pattern over bright grass,
        // antialiasing can pick up green from the background and look like a green outline.
        // Fix: lay down an opaque stone "base coat" slightly wider, then paint the pattern on top.
        if (stonePattern) {
          // Base coat (removes green fringe)
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.strokeStyle = "rgba(132, 122, 108, 0.92)";
          ctx.lineWidth = roadW + 2;
          for (let i = 0; i < segments.length; i += 1) {
            const s = segments[i];
            ctx.beginPath();
            ctx.moveTo(s.x1, s.y1);
            ctx.lineTo(s.x2, s.y2);
            ctx.stroke();
          }
          ctx.restore();

          // Pattern coat (texture)
          ctx.globalAlpha = 0.95;
          ctx.strokeStyle = stonePattern;
          ctx.lineWidth = roadW;
          for (let i = 0; i < segments.length; i += 1) {
            const s = segments[i];
            ctx.beginPath();
            ctx.moveTo(s.x1, s.y1);
            ctx.lineTo(s.x2, s.y2);
            ctx.stroke();
          }
        } else {
          ctx.globalAlpha = 1;
          ctx.strokeStyle = "rgba(120, 110, 96, 0.55)";
          ctx.lineWidth = roadW;
          for (let i = 0; i < segments.length; i += 1) {
            const s = segments[i];
            ctx.beginPath();
            ctx.moveTo(s.x1, s.y1);
            ctx.lineTo(s.x2, s.y2);
            ctx.stroke();
          }
        }
        ctx.restore();
      }

      // Lamps (terrain) — one per cell, top-right corner.
      // Turn ON during morning/noon/afternoon, OFF at night.
      {
        const isLampOn = String(sceneVariant || "") !== "night";
        const lampUrl = `${process.env.PUBLIC_URL || ""}/images/island/terrain/${isLampOn ? "lamp_on" : "lamp_off"}.png`;
        requestImage(lampUrl);
        const lampRec = getImageRecord(lampUrl);

        const { rows, cols, cellW, cellH, pad } = layout;
        const lampSize = Math.round(44 * BUILDING_SCALE); // 2x bigger
        const insetY = Math.round(ROAD_W / 2 + 8); // keep it off the road edge a bit
        const insetX = Math.max(4, Math.round(ROAD_W / 2 - 2)); // push further right so it doesn't overlap name plates

        // Only show lamps on occupied tiles (tiles that have a grid building).
        const occupied = new Set();
        (layout.buildings || []).forEach((b) => {
          const r = b?.tile?.r;
          const c = b?.tile?.c;
          if (isFinite(r) && isFinite(c)) occupied.add(`${r},${c}`);
        });

        for (let r = 0; r < rows; r += 1) {
          for (let c = 0; c < cols; c += 1) {
            if (!occupied.has(`${r},${c}`)) continue;
            const cellX = pad + c * cellW;
            const cellY = pad + r * cellH;
            const lx = cellX + cellW - insetX - lampSize;
            const ly = cellY + insetY;

            // Optional subtle glow when on
            if (isLampOn) {
              ctx.save();
              ctx.globalCompositeOperation = "lighter";
              ctx.globalAlpha = 0.25;
              const gx = lx + lampSize * 0.52;
              const gy = ly + lampSize * 0.35;
              const gr = lampSize * 0.55;
              const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, gr);
              g.addColorStop(0, "rgba(255, 244, 180, 0.55)");
              g.addColorStop(1, "rgba(255, 244, 180, 0)");
              ctx.fillStyle = g;
              ctx.beginPath();
              ctx.arc(gx, gy, gr, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            if (lampRec.status === "loaded" && lampRec.img) {
              ctx.drawImage(lampRec.img, lx, ly, lampSize, lampSize);
            } else {
              // Fallback: tiny post
              ctx.save();
              ctx.fillStyle = "rgba(120, 92, 62, 0.70)";
              roundRect(ctx, lx + lampSize * 0.6, ly + lampSize * 0.25, lampSize * 0.12, lampSize * 0.6, 6);
              ctx.fill();
              ctx.restore();
            }
          }
        }
      }

      ctx.restore();

      // Buildings
      const plateTheme = getPlateTheme(wrapRef.current);
      const hoverId = hoverTxnIdRef.current;
      const buildings = buildingsToDraw;
      for (let i = 0; i < buildings.length; i += 1) {
        const b = buildings[i];
        const tx = b.transaction;
        const isAction = b?.kind === "action";
        const buildingType = isAction ? null : getBuildingType(tx);
        const hoverKey = isAction ? `action:${b.action}` : `tx:${tx?.id}`;

        const selected = hoverId && String(hoverKey) === String(hoverId);
        const shadow = selected ? "rgba(15, 23, 42, 0.22)" : "rgba(0,0,0,0.14)";

        // Building sprite / emoji
        const buildingSpriteId = isAction
          ? (b.action === "create_transaction"
              ? "construction"
              : b.action === "visitor_center"
                ? "visitorCenter"
                : b.action === "game_center"
                  ? "gameCenter"
                : "harbor")
          : buildingType.id;
        const buildingEmoji = isAction
          ? (b.action === "create_transaction"
              ? "🏗️"
              : b.action === "visitor_center"
                ? "🏛️"
                : b.action === "game_center"
                  ? "🎮"
                : "⚓️")
          : buildingType.emoji;

        const buildingUrl = `${process.env.PUBLIC_URL || ""}/images/island/buildings/${buildingSpriteId}.png`;
        requestImage(buildingUrl);
        const buildingImgRec = getImageRecord(buildingUrl);

        // Put building art on the left, and badges (category + payer) on the right side.
        const spriteX = b.x + 6;
        const spriteY = b.y + 6;
        const spriteSize = Math.round(74 * BUILDING_SCALE);

        // Special buildings shimmer so they feel interactive/important.
        if (isAction) {
          drawSpecialBuildingShine(ctx, {
            x: spriteX,
            y: spriteY,
            w: spriteSize,
            h: spriteSize,
            ts,
            seed: `action:${b.action}`,
            layer: "under",
          });
        }

        // Amount plaque baseline (also used to position payer badge above it)
        const plaqueH = 22;
        const plaqueMargin = 25;
        const plaqueY = b.y + b.h - plaqueH - plaqueMargin;

        // Payer icon: left side, above the amount plaque
        const payerCx = b.x + 18;
        const payerCy = plaqueY - 10;

        // Hover affordance (no background card)
        if (selected) {
          ctx.save();
          ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
          ctx.lineWidth = 3;
          roundRect(ctx, spriteX - 4, spriteY - 4, spriteSize + 8, spriteSize + 8, 16);
          ctx.stroke();
          ctx.restore();
        }

        ctx.save();
        ctx.shadowColor = shadow;
        ctx.shadowBlur = selected ? 14 : 12;
        ctx.shadowOffsetY = selected ? 10 : 8;
        if (buildingImgRec.status === "loaded" && buildingImgRec.img) {
          ctx.drawImage(
            buildingImgRec.img,
            spriteX,
            spriteY,
            spriteSize,
            spriteSize
          );
        } else {
          ctx.fillStyle = "rgba(0, 0, 0, 0.76)";
          ctx.font = "44px system-ui, -apple-system, Segoe UI, sans-serif";
          ctx.textBaseline = "top";
          ctx.fillText(buildingEmoji, spriteX + 4, spriteY + 10);
        }
        ctx.restore();

        // Sheen overlay AFTER the sprite so it's unmistakably visible.
        if (isAction) {
          drawSpecialBuildingShine(ctx, {
            x: spriteX,
            y: spriteY,
            w: spriteSize,
            h: spriteSize,
            ts,
            seed: `action:${b.action}`,
            layer: "over",
          });
        }

        // Building name plate (themed, above the sprite)
        // Show for transaction buildings + special action buildings (Construction/Harbor/Visitor Center).
        if (!isAction || (b.action && b.action !== "")) {
          const displayName = String(
            isAction
              ? (b.action === "create_transaction"
                  ? "Construction"
                  : b.action === "visitor_center"
                    ? "Visitor Center"
                    : b.action === "game_center"
                      ? "Game Center"
                    : b.action === "invite_contributor"
                      ? "Harbor"
                      : "")
              : (buildingType?.name || "")
          ).trim();
          if (displayName) {
            ctx.save();
            const maxChars = 16;
            const text =
              displayName.length > maxChars ? `${displayName.slice(0, maxChars)}…` : displayName;

            const padX = 10;
            const h = 20;
            // Default: anchor to the sprite (not the whole hitbox) so the plate feels attached to the building.
            // Special-case Harbor: align to the top-left of its hitbox (inside the box), since it's a free-positioned building.
            const isHarbor = isAction && b.action === "invite_contributor";
            const y = isHarbor ? (b.y + 6) : (spriteY - h + 2);

            let fontSize = 11;
            ctx.font = `900 ${fontSize}px system-ui, -apple-system, Segoe UI, sans-serif`;
            let textW = ctx.measureText(text).width;
            const maxW = isHarbor ? Math.min(170, Math.max(92, b.w - 12)) : Math.min(150, Math.max(72, b.w + 14));
            while (fontSize > 9 && textW + padX * 2 > maxW) {
              fontSize -= 1;
              ctx.font = `900 ${fontSize}px system-ui, -apple-system, Segoe UI, sans-serif`;
              textW = ctx.measureText(text).width;
            }

            const w = Math.min(maxW, Math.ceil(textW + padX * 2));
            // Left-align the plate:
            // - For grid buildings: clamp within the cell (avoids covering the lamp).
            // - For Harbor (no tile): anchor to hitbox left.
            const x = isHarbor
              ? (b.x + 6)
              : (() => {
                  const cellX = layout.pad + (b?.tile?.c || 0) * layout.cellW;
                  const cellRight = cellX + layout.cellW;
                  return clamp(b.x + 6, cellX + 6, cellRight - w - 6);
                })();

            if (isAction) {
              // Animal Crossing-like plate for special buildings
              drawAnimalCrossingPlate(ctx, { x, y, w, h, text, textPadX: 12 });
            } else {
              ctx.shadowColor = "rgba(0,0,0,0.12)";
              ctx.shadowBlur = 10;
              ctx.shadowOffsetY = 6;
              ctx.fillStyle = plateTheme.bg;
              ctx.strokeStyle = plateTheme.border;
              ctx.lineWidth = 1;
              roundRect(ctx, x, y, w, h, 10);
              ctx.fill();
              ctx.stroke();

              ctx.shadowColor = "transparent";
              ctx.fillStyle = plateTheme.text;
              ctx.textAlign = "left";
              ctx.textBaseline = "middle";
              ctx.fillText(text, x + 12, y + h / 2 + 0.5);
            }
            ctx.restore();
          }
        }

        // Amount plaque (wooden sign) bottom-left of the building hitbox
        const plaqueText = isAction
          ? (b.action === "create_transaction"
              ? "New building"
              : b.action === "visitor_center"
                ? "Visitors"
                : b.action === "game_center"
                  ? "Games"
                : "Invite animal")
          : formatMoney(tx?.amount);

        if (plaqueText) {
          const plaquePadX = 8;
          const plaqueY = b.y + b.h - plaqueH - plaqueMargin;

          // Keep within the building hitbox (left-side space)
          const spaceLeft = b.x + 6;
          const spaceRight = b.x + b.w - 6;
          const maxPlaqueW = Math.max(44, Math.min(118, spaceRight - spaceLeft));

          ctx.save();
          let fontSize = 12;
          ctx.font = `900 ${fontSize}px system-ui, -apple-system, Segoe UI, sans-serif`;
          let textW = ctx.measureText(plaqueText).width;
          while (fontSize > 9 && textW + plaquePadX * 2 > maxPlaqueW) {
            fontSize -= 1;
            ctx.font = `900 ${fontSize}px system-ui, -apple-system, Segoe UI, sans-serif`;
            textW = ctx.measureText(plaqueText).width;
          }

          const plaqueW = Math.min(maxPlaqueW, Math.ceil(textW + plaquePadX * 2));
          const plaqueX = spaceLeft;

          // Wood gradient
          ctx.shadowColor = "rgba(0,0,0,0.18)";
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 6;
          const wood = ctx.createLinearGradient(plaqueX, plaqueY, plaqueX, plaqueY + plaqueH);
          wood.addColorStop(0, "rgba(186, 124, 70, 0.96)");
          wood.addColorStop(1, "rgba(122, 76, 40, 0.96)");
          ctx.fillStyle = wood;
          ctx.strokeStyle = "rgba(70, 42, 22, 0.55)";
          ctx.lineWidth = 1.5;
          roundRect(ctx, plaqueX, plaqueY, plaqueW, plaqueH, 10);
          ctx.fill();
          ctx.stroke();

          // Simple "grain" lines
          ctx.shadowColor = "transparent";
          ctx.globalAlpha = 0.18;
          ctx.strokeStyle = "rgba(255,255,255,0.9)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(plaqueX + 6, plaqueY + 8);
          ctx.lineTo(plaqueX + plaqueW - 6, plaqueY + 8);
          ctx.moveTo(plaqueX + 6, plaqueY + 14);
          ctx.lineTo(plaqueX + plaqueW - 6, plaqueY + 14);
          ctx.stroke();
          ctx.globalAlpha = 1;

          // Amount text
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(plaqueText, plaqueX + plaqueW / 2, plaqueY + plaqueH / 2 + 0.5);

          // Two tiny "nails"
          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.beginPath();
          ctx.arc(plaqueX + 8, plaqueY + 6, 1.4, 0, Math.PI * 2);
          ctx.arc(plaqueX + plaqueW - 8, plaqueY + 6, 1.4, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        }

        if (!isAction) {
          // Owner badge (payer)
          const payer = tx?.contributor;
          const payerEmoji =
            (payer?.icon && ICONS[payer.icon]) ||
            getStableAnimalEmoji(`payer:${payer?.id || payer?.name || tx?.contributorId || ""}`);

          const payerUrl = payer?.icon
            ? `${process.env.PUBLIC_URL || ""}/images/island/animals/${payer.icon}.png`
            : "";
          if (payerUrl) requestImage(payerUrl);
          const payerImgRec = payerUrl ? getImageRecord(payerUrl) : { status: "idle", img: null };

          // Payer badge (no background)
          ctx.save();
          ctx.shadowColor = "rgba(0,0,0,0.35)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;
          if (payerImgRec.status === "loaded" && payerImgRec.img) {
            const s = 18;
            ctx.drawImage(payerImgRec.img, payerCx - s / 2, payerCy - s / 2, s, s);
          } else {
            ctx.fillStyle = "rgba(0,0,0,0.88)";
            ctx.font = "16px system-ui, -apple-system, Segoe UI, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(payerEmoji, payerCx, payerCy);
          }
          ctx.restore();
        }
      }

      // Animals (contributors)
      const animalsToDraw = animalsRef.current;
      for (let i = 0; i < animalsToDraw.length; i += 1) {
        const a = animalsToDraw[i];
        const c = a.contributor;
        const emoji = (c?.icon && ICONS[c.icon]) || getStableAnimalEmoji(`c:${c?.id || c?.name || i}`);
        const displayName = String(c?.name || c?.displayName || c?.nickname || "").trim();
        const url = c?.icon ? `${process.env.PUBLIC_URL || ""}/images/island/animals/${c.icon}.png` : "";
        if (url) requestImage(url);
        const imgRec = url ? getImageRecord(url) : { status: "idle", img: null };

        // Shadow bubble
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.18)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 6;
        ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
        roundRect(ctx, a.x - 16, a.y - 16, 32, 32, 14);
        ctx.fill();
        ctx.restore();

        if (imgRec.status === "loaded" && imgRec.img) {
          ctx.drawImage(imgRec.img, a.x - 14, a.y - 14, 28, 28);
        } else {
          ctx.save();
          ctx.fillStyle = "rgba(0,0,0,0.78)";
          ctx.font = "18px system-ui, -apple-system, Segoe UI, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(emoji, a.x, a.y + 1);
          ctx.restore();
        }

        // Nameplate (above animal)
        if (displayName) {
          ctx.save();
          const maxChars = 14;
          const text = displayName.length > maxChars ? `${displayName.slice(0, maxChars)}…` : displayName;
          ctx.font = "700 11px system-ui, -apple-system, Segoe UI, sans-serif";
          const padX = 8;
          const textW = ctx.measureText(text).width;
          const w = Math.min(140, Math.max(40, Math.ceil(textW + padX * 2)));
          const h = 20;
          const x = a.x - w / 2;
          const y = a.y - 18 - h; // closer to the animal bubble

          ctx.shadowColor = "rgba(0,0,0,0.14)";
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 6;
          ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
          ctx.strokeStyle = "rgba(0,0,0,0.10)";
          roundRect(ctx, x, y, w, h, 10);
          ctx.fill();
          ctx.stroke();

          ctx.shadowColor = "transparent";
          ctx.fillStyle = "rgba(0,0,0,0.78)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(text, a.x, y + h / 2 + 0.5);
          ctx.restore();
        }
      }

      ctx.restore();
    };

    rafRef.current = window.requestAnimationFrame(render);
    return () => {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [layout, contributors, beach, roadNetwork, buildingsToDraw, sceneVariant]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getWorldPointFromEvent = (evt) => {
      const rect = canvas.getBoundingClientRect();
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;
      const { scale, ox, oy } = transformRef.current;
      const wx = (x - ox) / scale;
      const wy = (y - oy) / scale;
      return { wx, wy };
    };

    const onMove = (evt) => {
      const { wx, wy } = getWorldPointFromEvent(evt);
      // Prioritize animals for hover (they're visually above)
      const animals = animalsRef.current;
      let hoveredContributor = null;
      for (let i = animals.length - 1; i >= 0; i -= 1) {
        const a = animals[i];
        // Bubble is ~32x32 around (a.x, a.y)
        if (wx >= a.x - 16 && wx <= a.x + 16 && wy >= a.y - 16 && wy <= a.y + 16) {
          hoveredContributor = a?.contributor?.id;
          break;
        }
      }
      if (hoveredContributor) {
        hoverTxnIdRef.current = null;
        canvas.style.cursor = "pointer";
        return;
      }

      const buildings = buildingsToDraw;
        let hoveredTxn = null;
      let hoveredAction = null;
      for (let i = buildings.length - 1; i >= 0; i -= 1) {
        const b = buildings[i];
        if (wx >= b.x && wx <= b.x + b.w && wy >= b.y && wy <= b.y + b.h) {
            hoveredTxn = b?.kind === "action" ? `action:${b.action}` : `tx:${b.transaction?.id}`;
            hoveredAction = b?.kind === "action" ? b.action : null;
          break;
        }
      }
      hoverTxnIdRef.current = hoveredTxn;
      const disabledAction =
        isSettled &&
        (hoveredAction === "create_transaction" || hoveredAction === "invite_contributor");
      canvas.style.cursor = hoveredTxn ? (disabledAction ? "not-allowed" : "pointer") : "default";
    };

    const onClick = (evt) => {
      const { wx, wy } = getWorldPointFromEvent(evt);

      // Animals first
      const animals = animalsRef.current;
      for (let i = animals.length - 1; i >= 0; i -= 1) {
        const a = animals[i];
        if (wx >= a.x - 16 && wx <= a.x + 16 && wy >= a.y - 16 && wy <= a.y + 16) {
          onSelectContributor?.(a.contributor);
          return;
        }
      }

      const buildings = buildingsToDraw;
      for (let i = buildings.length - 1; i >= 0; i -= 1) {
        const b = buildings[i];
        if (wx >= b.x && wx <= b.x + b.w && wy >= b.y && wy <= b.y + b.h) {
          if (b?.kind === "action") {
            if (
              isSettled &&
              (b.action === "create_transaction" || b.action === "invite_contributor")
            ) {
              return;
            }
            if (b.action === "create_transaction") onCreateTransaction?.();
            if (b.action === "invite_contributor") onInviteContributor?.();
            if (b.action === "visitor_center") onOpenVisitorCenter?.();
            if (b.action === "game_center") onOpenGameCenter?.();
            return;
          }
          onSelectTransaction?.(b.transaction);
          return;
        }
      }
    };

    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("click", onClick);
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("click", onClick);
    };
  }, [
    layout.buildings,
    buildingsToDraw,
    isSettled,
    onSelectTransaction,
    onSelectContributor,
    onCreateTransaction,
    onInviteContributor,
    onOpenVisitorCenter,
    onOpenGameCenter,
  ]);

  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        height: `${wrapHeightPx}px`,
      }}
    >
      <canvas ref={canvasRef} aria-label="Island canvas" role="img" />
    </div>
  );
}

